import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Path, Warning } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { todayStr } from "@/lib/format";
import { DataTable } from "@/components/DataTable";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

/**
 * Patrol compliance — the question attendance cannot answer.
 *
 * Attendance says somebody was at the post. A client asking "prove your guard
 * walked the basement at 02:00" is asking something else entirely, and a guard
 * asleep in the shack has a perfect attendance record.
 *
 * ⚠️ Only rounds whose window has CLOSED are judged. Measuring a 07:00–19:00
 * shift at 14:00 against all six of its rounds reported 33% for a guard who was
 * exactly on schedule — every in-progress shift read as failing, which is the
 * fastest way to teach a supervisor to stop looking at this screen.
 */
export default function Tours() {
  const [date, setDate] = useState(todayStr());
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null);

  const load = (d) =>
    api.get(`/tours?date=${d}`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(date); }, [date]);

  const open = async (row) => {
    try {
      const { data: t } = await api.get(`/shifts/${row.shift_id}/tour`);
      setSel(t);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const columns = [
    { key: "ref_no", label: "Shift", render: (r) => (
      <div>
        <div className="font-mono text-xs">{r.ref_no}</div>
        <div className="text-[10px] text-muted-foreground">{r.start_time}–{r.end_time} · {r.shift_type}</div>
      </div>
    ) },
    { key: "site_name", label: "Site & post", render: (r) => (
      <div>
        <div className="font-semibold text-sm">{r.site_name}</div>
        <div className="text-[10px] text-muted-foreground">{r.post_name || "Any post"}</div>
      </div>
    ) },
    { key: "guard_name", label: "Guard", render: (r) => (
      r.guard_id
        ? r.guard_name
        : <div>
            <span className="text-muted-foreground">unassigned</span>
            <div className="text-[10px] text-muted-foreground leading-snug">
              not counted in compliance
            </div>
          </div>
    ) },
    { key: "complete_rounds", label: "Rounds", render: (r) => (
      <div>
        {/*
          "0/0" is not a score, it is a shift that has not started — and the
          handset already says "6 to come" for the same shift. Two screens
          describing one fact differently is worse than either wording.
        */}
        {r.rounds_due > 0
          ? <span className="font-mono text-sm font-bold">{r.complete_rounds}/{r.rounds_due}</span>
          : <span className="font-mono text-xs text-muted-foreground">none due yet</span>}
        {r.pending_rounds > 0 && (
          <span className="block text-[10px] text-muted-foreground">{r.pending_rounds} to come</span>
        )}
      </div>
    ) },
    { key: "compliance_pct", label: "Compliance", render: (r) => {
      const p = r.compliance_pct;
      if (p == null) return <span className="text-muted-foreground text-xs">nothing due yet</span>;
      const tone = p >= 100 ? "text-[hsl(var(--success))]" : p >= 75 ? "text-[hsl(var(--warning))]" : "text-[hsl(var(--alert))]";
      return <span className={`font-mono text-sm font-bold ${tone}`}>{p}%</span>;
    } },
    { key: "missed", label: "What was missed", render: (r) => (
      r.missed.length === 0
        ? <span className="text-xs text-muted-foreground">—</span>
        : <div className="text-[11px]">
            {r.missed.slice(0, 2).map((m) => (
              <div key={m.round}>
                <span className="font-mono">round {m.round} ({m.from})</span>{" "}
                <span className={m.state === "not_started" ? "text-[hsl(var(--alert))]" : "text-[hsl(var(--warning))]"}>
                  {m.state === "not_started" ? "never started" : "abandoned"}
                </span>
                {m.missed.length > 0 && <span className="text-muted-foreground"> — {m.missed.slice(0, 2).join(", ")}{m.missed.length > 2 ? "…" : ""}</span>}
              </div>
            ))}
            {r.missed.length > 2 && <div className="text-muted-foreground">+{r.missed.length - 2} more</div>}
          </div>
    ) },
  ];

  const pct = data?.compliance_pct;

  return (
    <div data-testid="page-tours">
      <PageHeader
        title="Guard tours"
        sub="Whether the route was walked, not just whether somebody turned up"
        actions={
          <Input type="date" data-testid="tours-date" className="rounded-none w-auto font-mono"
                 value={date} onChange={(e) => setDate(e.target.value)} />
        }
      />

      <div className="grid md:grid-cols-4 gap-3 mb-4">
        <Tile label="Shifts on patrol" value={data?.shifts_requiring_a_patrol ?? "—"} />
        <Tile label="Rounds due" value={data?.rounds_due ?? "—"} />
        <Tile label="Rounds complete" value={data?.complete_rounds ?? "—"} />
        <Tile label="Compliance" value={pct == null ? "—" : `${pct}%`}
              tone={pct == null ? null : pct >= 100 ? "success" : pct >= 75 ? "warning" : "alert"} />
      </div>

      {data?.unassigned_posts > 0 && (
        <div className="border border-border p-3 mb-4 text-xs text-muted-foreground" data-testid="tours-unassigned">
          <span className="text-foreground font-semibold">
            {data.unassigned_posts} of these posts {data.unassigned_posts > 1 ? "have" : "has"} no guard assigned
          </span>{" "}
          — their rounds are listed but kept out of the compliance figure. A route nobody was rostered to
          walk is a gap on the <Link to="/schedule" className="underline">Schedule</Link>, and counting it
          as a failed patrol blames the guards for it, in the number a client is shown.
        </div>
      )}

      {data && data.shifts_requiring_a_patrol === 0 && (
        <div className="border border-border p-4 text-xs text-muted-foreground mb-4">
          No shift on this day is on a site with a patrol route. A site has one when it carries
          checkpoints and a patrol interval — set both on <span className="text-foreground">Sites &amp; Posts</span>.
          A static reception post genuinely has no route, and leaving it without one is a real answer.
        </div>
      )}

      {pct != null && pct < 100 && (
        <div className="border border-[hsl(var(--warning))] bg-[hsl(var(--warning))]/10 p-3 mb-4 flex gap-2" data-testid="tours-banner">
          <Warning size={18} weight="fill" className="text-[hsl(var(--warning))] shrink-0 mt-0.5" />
          <div className="text-xs leading-snug">
            <span className="font-bold">
              {data.rounds_due - data.complete_rounds} of {data.rounds_due} due round(s) were not completed.
            </span>{" "}
            A round nobody started and a round abandoned halfway are different conversations — the column on
            the right says which. Supervisors were notified at each guard&apos;s check-out.
          </div>
        </div>
      )}

      <DataTable columns={columns} rows={data?.rows || []} onRowClick={open} testid="tours-table"
                 empty="Nothing to show for this day." />

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="rounded-none max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">
            {sel?.shift?.site_name} — {sel?.shift?.guard_name}
          </DialogTitle></DialogHeader>
          {sel && (
            <>
              <div className="text-xs text-muted-foreground mb-3">
                {sel.shift.date} · {sel.shift.start_time}–{sel.shift.end_time} · a round every {sel.interval_hours}h
                over {sel.checkpoints.length} checkpoint(s)
              </div>
              <div className="space-y-2">
                {sel.rounds.map((r) => (
                  <div key={r.round} className={`border p-3 ${
                    r.state === "complete" ? "border-border"
                      : r.state === "pending" ? "border-border border-dashed"
                        : "border-[hsl(var(--alert))]"}`} data-testid={`round-${r.round}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-xs font-bold">Round {r.round} · {r.from}–{r.to}</span>
                      <span className={`font-mono text-[10px] uppercase tracking-wider ${
                        r.state === "complete" ? "text-[hsl(var(--success))]"
                          : r.state === "pending" ? "text-muted-foreground"
                            : "text-[hsl(var(--alert))]"}`}>
                        {r.state === "pending" ? "not due yet" : `${r.scanned}/${r.of} · ${r.state.replace("_", " ")}`}
                      </span>
                    </div>
                    {r.scans.length > 0 && (
                      <div className="mt-2">
                        {r.scans.map((sc, i) => (
                          <div key={i} className="flex items-center justify-between border-t border-border py-1 text-[11px]">
                            <span>{sc.checkpoint}</span>
                            <span className="font-mono text-muted-foreground">
                              {sc.at}{sc.distance_m != null ? ` · ${sc.distance_m}m` : ""} · {sc.method}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                    {r.missed.length > 0 && r.state !== "pending" && (
                      <div className="mt-2 text-[11px] border-l-2 border-[hsl(var(--alert))] pl-2">
                        <span className="font-bold text-[hsl(var(--alert))]">Not scanned:</span> {r.missed.join(", ")}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Tile({ label, value, tone }) {
  const border = tone === "alert" ? "border-[hsl(var(--alert))]"
    : tone === "warning" ? "border-[hsl(var(--warning))]"
      : tone === "success" ? "border-[hsl(var(--success))]" : "border-border";
  return (
    <div className={`border ${border} bg-card p-3`}>
      <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">{label}</div>
      <div className="font-heading font-black text-2xl tabular-nums">{value}</div>
    </div>
  );
}
